import axios from 'axios';
import { apiClient } from '../client';
import { approvalEndpoints } from '../endpoints/approval.endpoints';
import type { ApiResponse } from '../types/envelope.types';
import type {
  ApprovalCount,
  ApprovalDecisionRequest,
  ApprovalDecisionResult,
  ApprovalDetail,
  ApprovalListPaged,
  ContributedApproval,
} from '../types/approval.types';

function throwFromEnvelope(err: unknown, fallback: string): never {
  if (axios.isAxiosError(err) && err.response?.data) {
    const body = err.response.data as ApiResponse<unknown>;
    throw new Error(body.message || err.message || fallback);
  }
  throw err instanceof Error ? err : new Error(fallback);
}

export const approvalService = {
  async getPendingCount(): Promise<ApprovalCount> {
    try {
      const res = await apiClient.get<ApiResponse<ApprovalCount>>(
        approvalEndpoints.count(),
      );
      const body = res.data;
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Failed to load approval count');
      }
      return body.data;
    } catch (err) {
      throwFromEnvelope(err, 'Failed to load approval count');
    }
  },

  async getList(
    status: string = 'APPROVAL_PENDING',
    page: number = 1,
    pageSize: number = 20,
  ): Promise<ApprovalListPaged> {
    try {
      const res = await apiClient.get<ApiResponse<ApprovalListPaged>>(
        approvalEndpoints.list({ status, page, pageSize }),
      );
      const body = res.data;
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Failed to load approvals');
      }
      return body.data;
    } catch (err) {
      throwFromEnvelope(err, 'Failed to load approvals');
    }
  },

  async getDetail(id: string): Promise<ApprovalDetail> {
    try {
      const res = await apiClient.get<ApiResponse<ApprovalDetail>>(
        approvalEndpoints.detail(id),
      );
      const body = res.data;
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Failed to load approval');
      }
      return body.data;
    } catch (err) {
      throwFromEnvelope(err, 'Failed to load approval');
    }
  },

  async approve(id: string, request: ApprovalDecisionRequest): Promise<ApprovalDecisionResult> {
    try {
      const res = await apiClient.post<ApiResponse<ApprovalDecisionResult>>(
        approvalEndpoints.approve(id),
        request,
      );
      const body = res.data;
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Approve failed');
      }
      return body.data;
    } catch (err) {
      throwFromEnvelope(err, 'Approve failed');
    }
  },

  async reject(id: string, request: ApprovalDecisionRequest): Promise<ApprovalDecisionResult> {
    try {
      const res = await apiClient.post<ApiResponse<ApprovalDecisionResult>>(
        approvalEndpoints.reject(id),
        request,
      );
      const body = res.data;
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Reject failed');
      }
      return body.data;
    } catch (err) {
      throwFromEnvelope(err, 'Reject failed');
    }
  },

  /** Other modules' items awaiting this sign-in (the restaurant's requisitions). */
  async getContributed(): Promise<ContributedApproval[]> {
    try {
      const res = await apiClient.get<ApiResponse<ContributedApproval[]>>(approvalEndpoints.contributed());
      const body = res.data;
      if (!body.success) throw new Error(body.message || 'Failed to load restaurant approvals');
      return body.data ?? [];
    } catch (err) {
      throwFromEnvelope(err, 'Failed to load restaurant approvals');
    }
  },

  /** Approve or reject one; a rejection needs the remarks. */
  async decideContributed(item: ContributedApproval, decision: 'approve' | 'reject', notes?: string): Promise<void> {
    try {
      const res = await apiClient.post<ApiResponse<unknown>>(
        approvalEndpoints.decideContributed(item.source, item.itemId, decision),
        { notes: notes || undefined, rowVersion: 0 },
      );
      if (!res.data.success) throw new Error(res.data.message || 'Decision failed');
    } catch (err) {
      throwFromEnvelope(err, 'Decision failed');
    }
  },
};
